import React, { useMemo } from 'react';
import { useSession } from '@clerk/clerk-react';
import { MassMailModal, Contact } from 'screens/mass-mail';
import { HelpdeskEmail, MassMailAiSuggestion } from 'screens/mass-mail/types';
import { generateRenoReplySuggestion } from 'shared/api/ai-reply-suggestion';
import { RENO_DEFAULT_AI_INSTRUCTION } from 'shared/utils/aiReplyInstructionDefaults';
import { RenoTicket } from '../types/renoTicket';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSend: (emailData: any) => Promise<any>;
  ticket: RenoTicket;
  // Bepaalt de standaard ontvanger: leverancier (RS) of huurder (RT).
  channel?: 'supplier' | 'tenant';
  // Expliciet gekozen ontvangers (multi-select bij de stuurknop). Overschrijven de standaard.
  recipients?: { name: string; email: string }[];
  emailHistory?: HelpdeskEmail[];
}

/**
 * Leveranciers van het ticket, in volgorde: de gekoppelde assignments (huidig model)
 * en als terugval het verouderde `supplier`-veld. Leveranciers zonder e-mailadres
 * blijven in de lijst staan met een leeg adres — de composer toont dan expliciet
 * dat er geen adres bekend is i.p.v. hen stil weg te laten.
 */
type SupplierLike = { id: number; name?: string; email?: string };

function collectSuppliers(ticket: RenoTicket): { id: number; name: string; email: string }[] {
  const seen = new Map<number, { id: number; name: string; email: string }>();

  (ticket.supplierAssignments ?? []).forEach((assignment) => {
    const supplier: SupplierLike | undefined = assignment.supplier;
    if (!supplier || seen.has(supplier.id)) return;
    seen.set(supplier.id, {
      id: supplier.id,
      name: supplier.name || 'Leverancier',
      email: supplier.email?.trim() || '',
    });
  });

  const legacy = ticket.supplier as SupplierLike | undefined;
  if (legacy && !seen.has(legacy.id)) {
    seen.set(legacy.id, {
      id: legacy.id,
      name: legacy.name || 'Leverancier',
      email: legacy.email?.trim() || '',
    });
  }

  return Array.from(seen.values());
}

function buildSupplierContact(ticket: RenoTicket): Contact | null {
  const suppliers = collectSuppliers(ticket);
  if (suppliers.length === 0) return null;
  // Eerste leverancier mét adres; anders de eerste, zodat zichtbaar wordt wie er geen heeft.
  const supplier = suppliers.find((s) => s.email) ?? suppliers[0];
  return {
    id: String(supplier.id),
    name: supplier.name,
    firstName: supplier.name,
    lastName: '',
    email: supplier.email,
    resourceId: String(supplier.id),
    phoneMobile: '',
    units: [],
    buildingId: ticket.renovationProject?.building?.id || '',
    buildingName: ticket.renovationProject?.title || '',
    unitId: '',
    unitNumber: '',
  };
}

function buildReporterContact(ticket: RenoTicket): Contact | null {
  const reporter = ticket.reporter as any;
  if (!reporter) return null;
  const firstName = reporter.firstName || '';
  const lastName = reporter.lastName || '';
  return {
    id: String(reporter.id),
    name: `${firstName} ${lastName}`.trim() || reporter.email || 'Melder',
    firstName,
    lastName,
    email: reporter.email || '',
    resourceId: String(reporter.id),
    phoneMobile: reporter.phoneMobile || reporter.phone_mobile || '',
    units: [],
    buildingId: ticket.renovationProject?.building?.id || '',
    buildingName: ticket.renovationProject?.title || '',
    unitId: '',
    unitNumber: '',
  };
}

export const RenoMassMailModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  onSend,
  ticket,
  channel = 'supplier',
  recipients,
  emailHistory,
}) => {
  const { session } = useSession();

  /**
   * De knop **AI-voorstel** boven het tekstvak.
   *
   * Alleen op vraag: bij een renovatieticket schrijft er niets automatisch mee, want
   * die communicatie loopt vooral met leveranciers en wordt door een medewerker
   * opgevolgd. Er is dus ook geen `loadPending` en niets af te sluiten — het
   * voorstel bestaat zolang dit venster open staat.
   *
   * Het kanaal gaat mee: de backend neemt het verloop van dát kanaal als context, en
   * een bericht aan een aannemer klinkt anders dan één aan een bewoner.
   */
  const aiSuggestion = useMemo<MassMailAiSuggestion | undefined>(() => {
    const ticketId = ticket?.id;
    if (!session || !ticketId) return undefined;
    return {
      generate: (instructie) =>
        generateRenoReplySuggestion(ticketId, channel, session, instructie),
      defaultInstruction: RENO_DEFAULT_AI_INSTRUCTION,
    };
  }, [session, ticket?.id, channel]);

  const initialState = useMemo(() => {
    const code = `${channel === 'tenant' ? 'RT' : 'RS'}-${ticket.id}`;

    // Expliciet gekozen ontvangers (multi-select) hebben voorrang.
    let contacts: Contact[];
    if (recipients?.length) {
      contacts = recipients.map((r) => ({
        id: r.email,
        name: r.name || r.email,
        firstName: r.name || r.email,
        lastName: '',
        email: r.email,
        resourceId: r.email,
        phoneMobile: '',
        units: [],
        buildingId: ticket.building?.id || ticket.renovationProject?.building?.id || '',
        buildingName: ticket.renovationProject?.title || '',
        unitId: '',
        unitNumber: '',
      }));
    } else {
      const supplierContact = buildSupplierContact(ticket);
      const reporterContact = buildReporterContact(ticket);
      const primaryContact = channel === 'tenant'
        ? (reporterContact ?? supplierContact)
        : (supplierContact ?? reporterContact);
      contacts = primaryContact ? [primaryContact] : [];
    }

    return {
      contacts,
      // Eén mail naar alle ontvangers: de eerste in TO, de rest per contact
      // instelbaar op CC (standaard) of BCC.
      enableRecipientTypes: true,
      emailSubject: `${code} - ${ticket.title}`,
      aiSuggestion,
      // Reno verstuurt altijd via leo@yally.be — afzender vastzetten (geen keuze).
      senderEmail: 'leo@yally.be',
      lockedSenderEmail: 'leo@yally.be',
      contextPlaceholders: {
        ticketId: code,
        ticketTitle: ticket.title || '',
        buildingId: ticket.building?.id || ticket.renovationProject?.building?.id || '',
        unitInfo: ticket.renovationProject ? ` - ${ticket.renovationProject.title}` : '',
      },
    };
  }, [ticket.id, ticket.title, ticket.supplier, ticket.supplierAssignments, ticket.reporter, ticket.renovationProject, ticket.building, channel, recipients, aiSuggestion]);

  if (!isOpen) return null;

  return (
    <MassMailModal
      isOpen={isOpen}
      onClose={onClose}
      onSuccess={onSuccess}
      onSend={onSend}
      initialState={initialState}
      emailHistory={emailHistory}
      // Na verzenden sluit de composer; het verzendrapport komt van de pagina zelf.
      closeOnSuccess
    />
  );
};
